import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEnrichmentState } from '../../hooks/useEnrichmentState';
import { userAppContext } from '../../context/Context';

import Alert from '../../global/Alert/Alert';
import LoadingSpinner from '../../global/Loader/LoaderSpinner';
import EnrichmentModal from './components/EnrichmentModal';
import ValidationMessage from './components/ValidationMessage';
import SharedModal from '../../components/Modal/SharedModal';
import { getItemCount } from '../../components/AdvancedSearch/AdvancedSearchHelpers';
import { UserContext } from '../../types';
import { advancedExamples } from '../../components/AdvancedSearch/AdvancedExamples';
import useFetchSuggestions from '../../hooks/useFetchSuggestions';
import EnrichmentSwarmPlot from './components/EnrichmentGraph';
import { geneFilterOptions, tabs } from './helpers';
import { useEnrichmentValidation, useEnrichmentAPI, useEnrichmentDownload } from './hooks';

const Enrichment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { enrichmentFrom, clearEnrichmentFrom } = useEnrichmentState();
  const { user, authorization, isAuthenticated } = userAppContext() as UserContext;

  const initialData = location.state || {};

  const [searchType, setSearchType] = useState(initialData.searchType || 'GENES');
  const [text, setText] = useState(initialData.text || '');
  const [geneFilterType, setGeneFilterType] = useState(
    initialData.selectedFilter || initialData.geneFilterType || 'geneSymbol',
  );

  const [showModal, setShowModal] = useState(false);
  const [showWarningAlert, setShowWarningAlert] = useState(false);
  const [trialLimitDismissed, setTrialLimitDismissed] = useState(false);

  const searchTypeRef = useRef(searchType);

  const isAcademic = user?.['cognito:groups'] ? user?.['cognito:groups'][0] === 'ROLE_ACADEMIC' : null;
  const isTrial = user?.['cognito:groups']?.[0] === 'ROLE_TRIAL';

  const [enrichmentParams, setEnrichmentParams] = useState({
    commonVariants: 2,
    commonGenes: 2,
    maxPvalue: 0.05,
    maxScore: 1,
    minScore: 0,
    pageNumber: 0,
    source: isAcademic ? 'CURATED' : 'ALL',
  });

  // Custom hooks
  const { validation, showValidation, validateInput, clearValidation } = useEnrichmentValidation({
    text,
    searchType,
    geneFilterType,
  });

  const {
    enrichmentLoading,
    enrichmentResults,
    showErrorAlert,
    showSuccessAlert,
    showApiKeyAlert,
    showTrialLimitModal,
    performEnrichment,
    clearResults,
    setShowApiKeyAlert,
    setShowTrialLimitModal,
  } = useEnrichmentAPI({
    authorization: authorization as string,
    isAuthenticated,
    enrichmentParams,
  });

  const {
    csvData,
    setCsvData,
    metadataContent,
    setMetadataContent,
    swarmRef,
    downloadZIP,
    downloadGraphPNG,
  } = useEnrichmentDownload();

  const { notFoundItems, fetchSuggestions } = useFetchSuggestions(
    searchType,
    searchTypeRef.current === 'GENES' ? geneFilterType : 'strID',
    authorization,
    isAuthenticated,
  );

  useEffect(() => {
    if (initialData.selectedFilter) {
      setGeneFilterType(initialData.selectedFilter);
    }
  }, [initialData.selectedFilter]);

  useEffect(() => {
    searchTypeRef.current = searchType;
    if (searchTypeRef.current === 'GENES' && 
        !(enrichmentFrom?.from === 'advancedSearchSelectedItems' && enrichmentFrom?.selectedFilter === 'name')) {
      setGeneFilterType('geneSymbol');
    }
  }, [searchType, enrichmentFrom]);

  useEffect(() => {
    if (enrichmentResults && swarmRef.current) {
      setTimeout(() => {
        swarmRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [enrichmentResults]);

  useEffect(() => {
    if (!isTrial) return;
    const inputs = text
      .split('\n')
      .map((line: string) => line.trim())
      .filter((line: string) => line);

    if (inputs.length > 10 && !showTrialLimitModal && !trialLimitDismissed) {
      setShowTrialLimitModal(true);
    } else if (inputs.length <= 10) {
      setShowTrialLimitModal(false);
      setTrialLimitDismissed(false);
    }
  }, [text, isTrial, showTrialLimitModal, trialLimitDismissed]);

  const handleQueryChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const lines = value.split('\n');

    if (lines.length > 4000) {
      const limitedValue = lines.slice(0, 4000).join('\n');
      setText(limitedValue);
    } else {
      setText(value);
    }
  };

  const clearInputText = () => {
    setText('');
    clearValidation();
    clearResults();
  };

  const showExamples = () => {
    const formatFilterType =
      searchTypeRef.current === 'GENES'
        ? geneFilterType.replace('Id', 'ID').replace('geneSymbol', 'symbol')
        : 'strID';

    const exampleItems = advancedExamples[searchTypeRef.current]?.[formatFilterType] || [];
    setText(exampleItems.join('\n'));
  };

  const handleGeneFilterTypeChange = (newFilterType: string) => {
    setGeneFilterType(newFilterType);
  };

  const handleAutoDetectSuggestion = () => {
    if (validation?.detectedType && searchType === 'GENES') {
      handleGeneFilterTypeChange(validation.detectedType);
    }
  };

  const handleSearchTypeChange = (newSearchType: string) => {
    setSearchType(newSearchType);

    if (newSearchType === 'VARIANTS' && text.trim()) {
      // Validation will be handled by the hook
      validateInput();
    }
  };

  const handleModalSubmit = async (params: any) => {
    setEnrichmentParams(params);
    setShowModal(false);

    const result = await performEnrichment(searchType, text, geneFilterType);
    if (result) {
      setCsvData(result.csvData);
      setMetadataContent(result.metadataData);
    }
  };

  const goBack = () => {
    if (enrichmentFrom?.from === 'advancedSearch' || enrichmentFrom?.from === 'advancedSearchSelectedItems') {
      const { searchType, selectedFilter, text } = enrichmentFrom;

      navigate('/advancedSearch', {
        state: { searchType, selectedFilter, text },
      });
    } else {
      navigate('/');
    }

    clearEnrichmentFrom();
  };

  const handleEnrichmentClick = async () => {
    if (validation && !validation.isValid) {
      alert('Please fix the validation errors before running enrichment');
      return;
    }

    const inputs = text
      .split('\n')
      .map((line: string) => line.trim())
      .filter((line: string) => line);

    await fetchSuggestions(inputs);
    setShowModal(true);
  };

  const handleDownloadResults = async () => {
    if (!csvData || !metadataContent) {
      alert('No data available to download');
      return;
    }

    try {
      await downloadZIP(csvData, metadataContent, searchType);
    } catch (error) {
      console.error('Error downloading results:', error);
      alert('Error downloading results. Please try again.');
    }
  };

  const handleDownloadGraph = async () => {
    try {
      await downloadGraphPNG();
    } catch (error) {
      console.error('Error downloading graph:', error);
      alert('Error downloading graph. Please try again.');
    }
  };

  const canRunEnrichment = text.trim() && (!validation || validation.isValid);

  const getAvailableGeneFilterOptions = () => {
    const baseOptions = [
      { value: 'geneSymbol', label: 'Gene symbol' },
      { value: 'ensemblId', label: 'Ensembl ID' },
      { value: 'uniprotId', label: 'UniProt ID' },
      { value: 'geneId', label: 'NCBI Gene ID' },
    ];

    if (enrichmentFrom?.from === 'advancedSearchSelectedItems' && enrichmentFrom?.selectedFilter === 'name') {
      return [
        { value: 'name', label: 'Gene name' },
        ...baseOptions,
      ];
    }

    return baseOptions;
  };

  const availableGeneFilterOptions = getAvailableGeneFilterOptions();

  return (
    <div className="container mx-auto px-4 py-8">
      {enrichmentLoading && <LoadingSpinner style={{ position: 'fixes' }} />}

      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-4">Disease Enrichment Analysis</h1>
        <p className="text-gray-600 mb-6">
          Perform enrichment analysis on your gene or variant lists to identify significant associations of
          diseases and phenotypes.
        </p>
      </div>

      <div className="multiple-filters">
        {/* Search Type Selection */}
        <div className="mb-6">
          <div className="flex gap-4 justify-center">
            {tabs.map(type => (
              <label key={type} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="searchType"
                  value={type}
                  checked={searchType === type}
                  onChange={e => handleSearchTypeChange(e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm font-medium">{type.toUpperCase()}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Gene/Variant identifier dropdown */}
        {searchType === 'GENES' ? (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Identifier type</label>
            <select
              value={geneFilterType}
              onChange={e => handleGeneFilterTypeChange(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              {availableGeneFilterOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {geneFilterType === 'name' ? (
              <p className="text-xs mt-1">
                Gene names from Advanced Search. Changing this filter will clear the current list.
              </p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">Add your search list (separate items by line):</p>
            )}
          </div>
        ) : (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Variant identifier</label>
            <input
              readOnly
              value="rs number"
              className="w-full p-2 border border-gray-300 rounded-md bg-gray-50"
              disabled
            />
            <p className="text-xs text-gray-500 mt-1">Add your search list (separate items by line):</p>
          </div>
        )}

        {/* Text Input Area */}
        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {geneFilterType === 'name' 
              ? 'Selected gene names from Advanced Search:' 
              : 'Add your search list (separate items by line):'}
          </label>
          <textarea
            placeholder={geneFilterType === 'name' 
              ? 'Gene names from Advanced Search (read-only)' 
              : `Enter ${searchType.toLowerCase()} (one per line)`}
            value={text}
            onChange={handleQueryChange}
            readOnly={geneFilterType === 'name'}
            className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              geneFilterType === 'name' 
                ? 'bg-gray-50 cursor-not-allowed' 
                : validation && !validation.isValid 
                  ? 'border-red-300 bg-red-50' 
                  : 'border-gray-300'
            }`}
            rows={8}
          />
          <div className="flex justify-between items-center mt-2">
            <p className="text-sm text-gray-500">Items: {getItemCount(text)}</p>
          </div>
        </div>

        {/* Validation Messages */}
        {showValidation && (
          <ValidationMessage validation={validation} onSuggestionClick={handleAutoDetectSuggestion} />
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap mb-6">
          <button onClick={goBack} className="filter-upload">
            Back
          </button>

          <button
            onClick={handleEnrichmentClick}
            disabled={enrichmentLoading || !canRunEnrichment || (isTrial && getItemCount(text) > 10)}
            className={`filter-upload ${
              !canRunEnrichment || (isTrial && getItemCount(text) > 10) ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            Run Enrichment
            {enrichmentLoading && <LoadingSpinner style={{ position: 'fixes' }} />}
          </button>

          <button onClick={clearInputText} className="filter-upload">
            Clear List
          </button>

          <button onClick={showExamples} className="filter-upload">
            Show Examples
          </button>
          {enrichmentResults && csvData && (
            <button onClick={handleDownloadResults} className="filter-upload">
              Download Results (ZIP)
            </button>
          )}
        </div>
      </div>

      {/* Enrichment Modal */}
      {showModal && (
        <EnrichmentModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSubmit={handleModalSubmit}
          initialParams={enrichmentParams}
          searchType={searchType}
          notFoundItems={notFoundItems}
          user={user}
        />
      )}

      {/* Alerts */}
      {showErrorAlert && (
        <Alert
          message={`There was an error running the ${searchType.toLowerCase()} enrichment analysis.`}
          type="error"
        />
      )}

      {showWarningAlert && (
        <Alert
          message="There are no data available for this enrichment analysis with the provided identifiers."
          type="warning"
        />
      )}

      {showSuccessAlert && <Alert message="Enrichment analysis completed successfully." type="success" />}

      {showApiKeyAlert && (
        <Alert message="You need to set your API key." type="warning">
          <span
            onClick={() => navigate('/Profile-area#apiKey')}
            className="font-bold ml-2 cursor-pointer hover:text-blue-600"
          >
            Set API Key
          </span>
        </Alert>
      )}

      {/* Enrichment Results Visualization */}
      <div>
        {enrichmentResults && (
          <EnrichmentSwarmPlot ref={swarmRef} data={enrichmentResults} onDownload={handleDownloadGraph} />
        )}
      </div>

      {/* Trial Limit Modal */}
      <SharedModal
        open={showTrialLimitModal}
        setOpen={value => {
          setShowTrialLimitModal(value);
          if (!value) {
            setTrialLimitDismissed(true);
          }
        }}
        title="Trial User Limit"
        content={
          <div className="mt-4">
            <p className="text-gray-700 mb-4">
              Trial users can only run enrichment analysis with up to <strong>10 items</strong>, but will
              receive results for up to <strong>30 items</strong> from the analysis.
            </p>
            <p className="text-gray-700 mb-4">
              You currently have <strong>{getItemCount(text)} items</strong> in your list.
            </p>
            <p className="text-gray-600 mb-6">
              To analyze larger datasets (up to 4,000 items), please upgrade your plan.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowTrialLimitModal(false);
                  setTrialLimitDismissed(true);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowTrialLimitModal(false);
                  setTrialLimitDismissed(true);
                  navigate('/plans');
                }}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                View Plans
              </button>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default Enrichment;
